export function parseCommaName(
  username: string,
): { last: string; first: string } | null {
  const idx = username.indexOf(',');
  if (idx < 0) {
    return null;
  }

  const last = username.slice(0, idx).trim();
  const first =
    username
      .slice(idx + 1)
      .trim()
      .replace(/\./g, '')
      .split(/\s+/)[0] ?? '';
  if (!last || !first) {
    return null;
  }

  return { last, first };
}

export function playerNameKey(username: string): string {
  const parsed = parseCommaName(username);
  if (!parsed) {
    return username.trim().toLowerCase();
  }

  return `${parsed.last.toLowerCase()},${parsed.first.charAt(0).toLowerCase()}`;
}

function firstNamePartLength(username: string): number {
  const parsed = parseCommaName(username);
  if (!parsed) {
    return username.length;
  }

  return parsed.first.length;
}

export function preferredDisplayName(...names: string[]): string {
  return names.reduce((best, current) => {
    if (!best) {
      return current;
    }

    const currentFirstLen = firstNamePartLength(current);
    const bestFirstLen = firstNamePartLength(best);
    if (currentFirstLen > bestFirstLen) {
      return current;
    }
    if (currentFirstLen < bestFirstLen) {
      return best;
    }

    return current.length >= best.length ? current : best;
  });
}

export function formatPlayerLabel(player: {
  title?: string;
  username: string;
}): string {
  const name = player.username;
  return player.title ? `${player.title} ${name}` : name;
}

const capitalizeNamePart = (part: string): string =>
  part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part;

/**
 * SERP / breadcrumb label: `Carlsen,Magnus` → Magnus Carlsen,
 * `Carlsen,M` / `carlsen-m` → M. Carlsen. Lichess handles stay as the slug/username.
 */
export function formatPlayerSerpDisplayName(
  slugOrUsername: string,
  options?: { masterStyle?: boolean },
): string {
  const raw = slugOrUsername.trim();
  if (!raw) {
    return raw;
  }

  const comma = parseCommaName(raw);
  if (comma) {
    const last = comma.last
      .split(/\s+/)
      .map(capitalizeNamePart)
      .join(' ');
    if (comma.first.length === 1) {
      return `${comma.first.toUpperCase()}. ${last}`;
    }
    return `${capitalizeNamePart(comma.first)} ${last}`;
  }

  const masterStyle = options?.masterStyle !== false && raw.includes('-');
  if (masterStyle) {
    const parts = raw.toLowerCase().split('-').filter(Boolean);
    if (parts.length >= 2) {
      const first = parts[parts.length - 1];
      const last = parts.slice(0, -1).map(capitalizeNamePart).join(' ');
      if (first.length === 1) {
        return `${first.toUpperCase()}. ${last}`;
      }
      return `${capitalizeNamePart(first)} ${last}`;
    }
  }

  return raw;
}

export function formatPlayerSerpPossessive(displayName: string): string {
  if (!displayName) {
    return displayName;
  }
  return displayName.endsWith('s') || displayName.endsWith('S')
    ? `${displayName}'`
    : `${displayName}'s`;
}

export function resolveGamePlayerName(
  cellName: string,
  canonicalPlayer?: string,
): string {
  if (!canonicalPlayer) {
    return cellName;
  }

  if (playerNameKey(cellName) === playerNameKey(canonicalPlayer)) {
    return preferredDisplayName(cellName, canonicalPlayer);
  }

  return cellName;
}

export function resolveGamePlayerDisplayName(
  cellName: string,
  canonicalPlayer?: string,
  abbreviate = true,
): string {
  const resolved = resolveGamePlayerName(cellName, canonicalPlayer);
  return abbreviate ? abbreviatePlayerDisplayName(resolved) : resolved;
}

function abbreviateGivenNameToken(token: string): string {
  const stripped = token.replace(/\./g, '').trim();
  if (!stripped) {
    return token;
  }
  if (stripped.length === 1) {
    return `${stripped}.`;
  }
  return `${stripped.charAt(0).toUpperCase()}.`;
}

/** Shorten first/middle names for compact board headers. */
export function abbreviatePlayerDisplayName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return trimmed;
  }

  // Dual / alliance labels (e.g. "Duke Karl / Count Isouard").
  if (trimmed.includes('/')) {
    return trimmed
      .split(/\s*\/\s*/)
      .filter(Boolean)
      .map((part) => abbreviatePlayerDisplayName(part))
      .join(' / ');
  }

  const commaIndex = trimmed.indexOf(',');
  if (commaIndex >= 0) {
    const last = trimmed.slice(0, commaIndex).trim();
    const givenPart = trimmed.slice(commaIndex + 1).trim();
    if (!last || !givenPart) {
      return trimmed;
    }
    const initials = givenPart
      .split(/\s+/)
      .filter(Boolean)
      .map(abbreviateGivenNameToken);
    return `${last}, ${initials.join(' ')}`;
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return trimmed;
  }

  const last = parts[parts.length - 1];
  const initials = parts
    .slice(0, -1)
    .map(abbreviateGivenNameToken);
  return `${initials.join(' ')} ${last}`;
}

export function formatBoardPlayerLabel(
  name: string | undefined,
  elo: number | string | undefined,
  fallback: string,
): string {
  const displayName = name?.trim()
    ? abbreviatePlayerDisplayName(name)
    : fallback;
  return elo != null && elo !== ''
    ? `${displayName} (${elo})`
    : displayName;
}

export function formatBoardPlayersLine(
  white: string | undefined,
  whiteElo: number | string | undefined,
  black: string | undefined,
  blackElo: number | string | undefined,
): string {
  return `${formatBoardPlayerLabel(white, whiteElo, 'White')} vs ${formatBoardPlayerLabel(black, blackElo, 'Black')}`;
}

export function formatFullBoardPlayersLine(
  white: string | undefined,
  whiteElo: number | string | undefined,
  black: string | undefined,
  blackElo: number | string | undefined,
): string {
  const formatFull = (
    name: string | undefined,
    elo: number | string | undefined,
    fallback: string,
  ): string =>
    elo != null && elo !== ''
      ? `${name ?? fallback} (${elo})`
      : (name ?? fallback);
  return `${formatFull(white, whiteElo, 'White')} vs ${formatFull(black, blackElo, 'Black')}`;
}
